// src/packages/dhipaya/motor/dhipaya_car_shortterm_180d/DhipayaShort180Details.jsx
import React, { useState } from "react";
import "./dhipaya_short180.css";

/**
 * Dhipaya 2+ / 3+ Short-term (180 days)
 * Zero-index: ไม่มีตัวแปรชื่อ index ใน JSX/map
 * อ้างอิงข้อมูลสรุปจากเอกสารบริษัท (เวอร์ชัน 21.04.2025)
 */

const money = (n) => (typeof n === "number" ? n.toLocaleString() : n);

// สลับแผน 2+ / 3+
const TABS = [
  { key: "3p", label: "3+ (ชนกับยานพาหนะทางบก)" },
  { key: "2p", label: "2+ (รวมหาย/ไฟไหม้ + ชนกับยานพาหนะทางบก)" },
];

// วงเงินความคุ้มครองหลัก (สรุปจากเอกสาร)
const benefits = [
  { key: "own", name: "ความเสียหายรถตนเอง", v3p: "100,000", v2p: "100,000" },
  { key: "theft", name: "สูญหาย/ไฟไหม้",     v3p: "-",       v2p: "100,000" },
  { key: "tp_bod", name: "บาดเจ็บ/เสียชีวิตบุคคลภายนอก (บาท/คน)", v3p: "500,000", v2p: "500,000" },
  { key: "tp_bod_max", name: "บาดเจ็บ/เสียชีวิตรวม (บาท/ครั้ง)",   v3p: "10,000,000", v2p: "10,000,000" },
  { key: "tp_prop", name: "ทรัพย์สินบุคคลภายนอก (บาท/ครั้ง)",     v3p: "1,000,000", v2p: "1,000,000" },
  { key: "ry01", name: "ร.ย.01 อุบัติเหตุส่วนบุคคล (บาท/คน)",     v3p: "100,000", v2p: "100,000" },
  { key: "ry02", name: "ร.ย.02 ค่ารักษาพยาบาล (บาท/คน)",           v3p: "100,000", v2p: "100,000" },
  { key: "ry03", name: "ร.ย.03 ผู้ขับขี่ (บาท/ครั้ง)",              v3p: "200,000", v2p: "200,000" },
];

// ตารางเบี้ยตัวอย่างจากเอกสาร (ย่อให้อ่านง่าย)
const premiums = [
  { code: "110", group: "ที่นั่ง 3,4,5", p3p: 3560, p2p: 4060 },
  { code: "110", group: "ที่นั่ง 2",     p3p: 3959, p2p: 4460 },
  { code: "210", group: "12 ที่นั่ง",     p3p: 3959, p2p: 4460 },
  { code: "320", group: "กระบะ ≤ 3 ตัน",  p3p: 4359, p2p: 4859 },
];

// เงื่อนไขการรับประกันหลัก (ย่อ)
const conditions = [
  "อายุรถที่รับประกันภัยไม่เกิน 25 ปี",
  "รถใช้งานส่วนบุคคลเท่านั้น (ไม่รับจ้าง/ไม่ให้เช่า)",
  "ราคารถต้องไม่ต่ำกว่าทุนประกันที่เลือก",
  "เบี้ยรวมภาษีอากรแล้ว (ไม่รวม พ.ร.บ.)",
  "เงื่อนไขเป็นไปตามที่บริษัทกำหนด",
];

export default function DhipayaShort180Details({ onQuote }) {
  const [tab, setTab] = useState("3p");

  return (
    <div className="pkg-detail dpy-short180">
      <h2>ทิพย — 2+ / 3+ ระยะสั้น 180 วัน</h2>
      <p className="pkg-note">
        ระยะเวลาคุ้มครอง 180 วัน เหมาะกับลูกค้าที่ต้องการความคุ้มครองชั่วคราว
        วงเงินหลักและแนบท้ายตามตารางด้านล่าง (อ้างอิงเอกสารบริษัท)
      </p>

      {/* Tabs */}
      <div className="pkg-tabs" role="tablist" aria-label="เลือกประเภทความคุ้มครอง">
        {TABS.map((t) => (
          <button
            key={`tab-${t.key}`}
            role="tab"
            aria-selected={tab === t.key}
            className={`pkg-tab ${tab === t.key ? "is-active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Benefits table */}
      <table className="pkg-table">
        <thead>
          <tr>
            <th>ความคุ้มครอง</th>
            <th>{tab === "3p" ? "วงเงิน 3+" : "วงเงิน 2+"}</th>
          </tr>
        </thead>
        <tbody>
          {benefits.map((b) => (
            <tr key={`bf-${b.key}`}>
              <td>{b.name}</td>
              <td>{tab === "3p" ? b.v3p : b.v2p}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Premiums table */}
      <h3 className="pkg-section">เบี้ยประกัน (180 วัน)</h3>
      <table className="pkg-table">
        <thead>
          <tr>
            <th>โค้ดรถ</th>
            <th>กลุ่ม/เงื่อนไข</th>
            <th>เบี้ย 3+ (บาท)</th>
            <th>เบี้ย 2+ (บาท)</th>
          </tr>
        </thead>
        <tbody>
          {premiums.map((r) => (
            <tr key={`pr-${r.code}-${r.group}`}>
              <td>{r.code}</td>
              <td>{r.group}</td>
              <td>{money(r.p3p)}</td>
              <td>{money(r.p2p)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="pkg-section">เงื่อนไขสำคัญ</h3>
      <ul className="pkg-bullets">
        {conditions.map((t) => <li key={`cd-${t}`}>{t}</li>)}
      </ul>

      <div className="pkg-foot">
        <p className="pkg-note">
          * โปรดอ่านกรมธรรม์ฉบับเต็มเพื่อทราบรายละเอียดและข้อยกเว้นทั้งหมด
        </p>
        <button className="pkg-quote-btn" onClick={onQuote} aria-label="ขอใบเสนอราคา ทิพย 180 วัน">
          ขอใบเสนอราคา
        </button>
      </div>
    </div>
  );
}
